import { Link, useLocation, useNavigate } from "react-router-dom";
import { createElement, Fragment, useEffect, useState } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { ConditionalRenderer } from "components/conditional-renderer";
import {
  checkForOpenMenu,
  handleActivMenuBasedOnRoute,
  handleActivRouteBasedOnDetailRoute,
  isLinkToShowBasedOnRole,
  isMainMenu,
  isSubMenu,
} from "./helpers";
import {
  companyData,
  sidebarState,
  sidebarToggleState,
  userDetailState,
  userPermissionState,
  userSettingState,
} from "store/selectors";
import { SettingsIcon } from "assets/svgs";
import { onToggle } from "store/slices/sidebarSlice";
import { ViewSettingsPopover } from "./view-popover";
import logo from "assets/images/light_mode_transparent_logo.png";
import darkLogo from "assets/images/dark_transparent_logo.png";
import {
  activityKey,
  bulkOperation,
  importIndividualProperty,
  importKey,
  importLeads,
  importProperty,
  menuItems,
  projectProperties,
} from "./constants";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { fetchUserSettings } from "store/actions";
import { userRoleObj } from "utils/constants";
import styles from "./styles.module.css";

export function SideNav() {
  const navigate = useNavigate();
  const { company } = useAppSelector(companyData);
  const { settings, isDarkMode } = useAppSelector(userSettingState);
  const { permissions } = useAppSelector(userPermissionState);
  const { userDetail } = useAppSelector(userDetailState);
  const sidebarCurrentState = useAppSelector(sidebarState);
  const sidebarCurrentToggleState = useAppSelector(sidebarToggleState);
  const [showPopover, setShowPopover] = useState<any>(false);
  const [sidebarLinks, setSidebarLinks] = useState<any>(menuItems);
  const [openMenu, setOpenMenu] = useState<any>({
    menu: null,
    submenu: null,
    subsubmenu: null,
  });

  const dispatch = useAppDispatch();
  const { pathname, search } = useLocation();
  const filteredLinks = sidebarLinks.filter((linkObj: any) => linkObj.show);

  useEffect(() => {
    if (userDetail && settings) {
      setSidebarLinks(
        menuItems.map((item: any) => {
          const show: boolean = settings?.[0]?.[item?.backend_key] ?? true;

          if ([bulkOperation]?.includes(item?.key)) {
            return {
              ...item,
              show: userDetail?.role !== userRoleObj.user ? show : false,
            };
          }

          if (item?.key === importKey) {
            if (
              !permissions[0]?.can_import_lead &&
              !permissions[0]?.can_import_project_property &&
              !permissions[0]?.can_import_non_project_property
            ) {
              return {
                ...item,
                show: false,
              };
            }
            return {
              ...item,
              show,
              submenu: item?.submenu.filter((state: any) => {
                if (state?.key === importLeads) {
                  return permissions[0]?.can_import_lead;
                }

                if (state?.key === importProperty) {
                  return permissions[0]?.can_import_project_property;
                }

                if (state?.key === importIndividualProperty) {
                  return permissions[0]?.can_import_non_project_property;
                }

                return true;
              }),
            };
          }

          return { ...item, show };
        })
      );
    }
  }, [userDetail, settings, permissions]);

  useEffect(() => {
    dispatch(
      fetchUserSettings({
        userId: userDetail?.id,
      })
    );
  }, [userDetail]);

  useEffect(() => {
    if (checkForOpenMenu({ pathname })) {
      return setOpenMenu({
        menu: null,
        submenu: null,
        subsubmenu: null,
      });
    }

    filteredLinks.forEach((item: any) => {
      if (item.isSubmenu) {
        item.submenu.forEach((subItem: any) => {
          if (subItem?.isSubmenu) {
            subItem.submenu.forEach((subSubItem: any) => {
              if (subSubItem?.isSubmenu) {
                subSubItem.submenu.forEach((subSubSubItem: any) => {
                  if (subSubSubItem.path === pathname) {
                    setOpenMenu({
                      menu: item?.key,
                      submenu: subItem?.key,
                      subsubmenu: subSubItem?.key,
                    });
                  }
                });
              }
              if (subSubItem.path === pathname) {
                setOpenMenu({
                  menu: item?.key,
                  submenu: subItem?.key,
                  subsubmenu: null,
                });
              }
            });
          }

          if (subItem.path === pathname) {
            setOpenMenu({
              menu: item?.key,
              submenu: null,
              subsubmenu: null,
            });
          }
        });
      }
    });
  }, [pathname]);

  // useEffect(() => {
  //   if (sidebarCurrentState) {
  //     setOpenMenu({
  //       menu: null,
  //       submenu: null,
  //     });
  //   }
  // }, [sidebarCurrentState]);

  const renderMenuItems = (items: any) => {
    return items.map((item: any) => {
      const menuIcon = item.icon ? createElement(item.icon) : <></>;
      if (item.isSubmenu) {
        return (
          <SubMenu
            key={item.key}
            title={item.label}
            label={item.label}
            icon={menuIcon}
            active={handleActivMenuBasedOnRoute({ search, item, pathname })}
            open={
              openMenu.menu === item.key ||
              openMenu.submenu === item.key ||
              openMenu.subsubmenu === item.key
            }
            onClick={() => {
              if (isMainMenu(item.key)) {
                let path: null | string = null;
                if (item?.key === projectProperties) {
                  path = "/project-properties";
                } else {
                  path = item?.submenu[0]?.path;
                }

                path && navigate(path);
              }
            }}
            onOpenChange={() => {
              if (isMainMenu(item.key)) {
                return setOpenMenu({
                  menu: item.key === openMenu.menu ? null : item.key,
                });
              }

              if (isSubMenu(item?.key)) {
                return setOpenMenu({
                  ...openMenu,
                  submenu: openMenu.submenu === item.key ? null : item.key,
                });
              }

              setOpenMenu({
                ...openMenu,
                subsubmenu: openMenu.subsubmenu === item.key ? null : item.key,
              });
            }}
          >
            {renderMenuItems(item.submenu)} {/* Recursively render submenu */}
          </SubMenu>
        );
      }

      return (
        <ConditionalRenderer
          condition={isLinkToShowBasedOnRole({
            key: item?.key,
            role: userDetail?.role,
          })}
          key={item?.key}
        >
          <MenuItem
            title={item.label}
            key={item.key}
            icon={menuIcon}
            component={<Link to={item.path} />}
            active={
              item.path === pathname
              // ||handleActivRouteBasedOnDetailRoute({ item, pathname })
            }
          >
            {item.label}
          </MenuItem>
        </ConditionalRenderer>
      );
    });
  };

  return (
    <Fragment>
      <Sidebar
        className={`${styles.sidebarMain} ${isDarkMode ? styles.darkMode : ""}`}
        // backgroundColor={isDarkMode ? "#16161d" : "#0D2533"}
        backgroundColor={isDarkMode ? "#162033" : "#155E75"}
        width="300px"
        collapsedWidth="80px"
        collapsed={sidebarCurrentState}
        breakPoint="lg"
        transitionDuration={500}
        toggled={sidebarCurrentToggleState}
        onBackdropClick={() => {
          dispatch(onToggle(!sidebarCurrentToggleState));
        }}
        rootStyles={{
          width: "22%",
          minWidth: "220px",
          maxWidth: "300px",
          flexShrink: 0,
        }}
      >
        <div className={isDarkMode ? styles.darkLogo : styles.logo}>
          <div className={styles.img}>
            <img src={isDarkMode ? darkLogo : logo} alt="company's-logo" />
          </div>
          <ConditionalRenderer condition={!sidebarCurrentState}>
            <h1>Sale SpeedUp</h1>
          </ConditionalRenderer>
        </div>
        <Menu className={styles.menuContainer} closeOnClick={true}>
          {renderMenuItems(filteredLinks)}

          <MenuItem
            title={"Settings"}
            icon={<SettingsIcon />}
            rootStyles={{
              marginTop: "auto",
              backgroundColor: isDarkMode ? "#1e293b" : "#0E7490",
            }}
            onClick={() => setShowPopover(!showPopover)}
          >
            Settings
          </MenuItem>
        </Menu>
      </Sidebar>
      <ConditionalRenderer condition={showPopover}>
        <ViewSettingsPopover
          showModal={showPopover}
          onClose={() => setShowPopover(false)}
        />
      </ConditionalRenderer>
    </Fragment>
  );
}
